import React, { createContext, useContext, useEffect } from 'react';

import { JDPModuleName, JDPModuleStatus, JobPostingModuleConfig } from '@/components/job-posting/types';

type Action = {
  type: 'UPDATE_MODULE_STATUS';
  moduleName: JDPModuleName;
  status: JDPModuleStatus;
};

type Dispatch = (action: Action) => void;

type JobPostingTrackingState = {
  modules: JobPostingModuleConfig[];
  isJobPostingLoaded: boolean;
};

const isLoadingModule = ({ status }: JobPostingModuleConfig): boolean => status === 'LOADING';
const isLoadingModules = (modules: JobPostingModuleConfig[]): boolean => modules.some(isLoadingModule);

const trackingReducer = (state: JobPostingTrackingState, action: Action): JobPostingTrackingState => {
  const { modules } = state;

  switch (action.type) {
    case 'UPDATE_MODULE_STATUS': {
      const { moduleName, status } = action;
      const index = modules.findIndex(({ name }: JobPostingModuleConfig): boolean => name === moduleName);

      if (index >= 0 && modules[index].status !== status) {
        const updatedModules = modules.map((module) => {
          if (module.name === moduleName) {
            return { ...module, status };
          }
          return { ...module };
        });

        return { modules: updatedModules, isJobPostingLoaded: !isLoadingModules(updatedModules) };
      }

      return state;
    }
    default: {
      return state;
    }
  }
};

const JobPostingModuleDispatchContext = createContext<Dispatch | undefined>(undefined);

type JobPostingModuleStatusProviderProps = {
  modules: JobPostingModuleConfig[];
  notifyVisibleModuleList: (modules: JDPModuleName[]) => void;
};

export const JobPostingModuleStatusProvider: React.FC<JobPostingModuleStatusProviderProps> = ({
  modules,
  notifyVisibleModuleList,
  children,
}) => {
  const [state, dispatch] = React.useReducer(trackingReducer, {
    modules,
    isJobPostingLoaded: !isLoadingModules(modules),
  });

  useEffect(() => {
    if (state.isJobPostingLoaded) {
      notifyVisibleModuleList(state.modules.filter(({ status }) => status === 'VISIBLE').map(({ name }) => name));
    }
  }, [state, notifyVisibleModuleList]);

  return (
    <JobPostingModuleDispatchContext.Provider value={dispatch}>{children}</JobPostingModuleDispatchContext.Provider>
  );
};

export const useJobPostingModuleDispatcher = (): Dispatch => {
  const dispatch = useContext(JobPostingModuleDispatchContext);
  if (typeof dispatch === 'undefined') {
    throw new Error('useJobPostingModuleDispatcher must be used within a JobPostingModuleStatusProvider');
  }
  return dispatch;
};
